import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';

import { useTabbar } from '@common/providers/useTabbar';
import { BottomSheet } from '@common/ui/BottomSheet';
import { Button } from '@common/ui/buttons/Button';
import { Card } from '@common/ui/Card';
import { Input } from '@common/ui/inputs/Input';
import { CellSelect } from '@common/ui/selection';
import { Heading } from '@common/ui/typography/Heading';
import { Paragraph } from '@common/ui/typography/Paragraph';
import { useEventEmit } from '@common/utils/events';
import { useRequestSubmit } from '@common/utils/requestSubmitPolyfill';

import { useAuthStore } from '@entities/auth/stores/useAuthStore';
import {
  isStorableUser,
  readStoredUser,
  reportRefusedUser,
} from '@entities/auth/utils';
import * as dicts from '@entities/profile/dicts';

import { usePostProfileUpdate } from '@services/hooks/UsersService';

const heightSchema = z.object({
  height: z
    .number({ message: 'Введите рост' })
    .min(140, 'Минимум 140 см')
    .max(210, 'Максимум 210 см'),
});

const alcoholSchema = z.object({
  alcohol: z
    .string({ message: 'Укажите ваше отношение к алкоголю' })
    .min(1, 'Укажите ваше отношение к алкоголю'),
});

const smokeSchema = z.object({
  smoke: z
    .string({ message: 'Укажите ваше отношение к курению' })
    .min(1, 'Укажите ваше отношение к курению'),
});

const sportSchema = z.object({
  sport: z
    .string({ message: 'Укажите ваше отношение к спорту' })
    .min(1, 'Укажите ваше отношение к спорту'),
});

const childrenSchema = z.object({
  children: z
    .string({ message: 'Укажите наличие детей' })
    .min(1, 'Укажите наличие детей'),
});

type FormData = z.infer<typeof heightSchema> &
  z.infer<typeof alcoholSchema> &
  z.infer<typeof smokeSchema> &
  z.infer<typeof sportSchema> &
  z.infer<typeof childrenSchema>;

const LAST_STEP = 4;

export const Short: React.FC = () => {
  const tabbar = useTabbar();
  const emit = useEventEmit();
  const navigate = useNavigate();

  const formRef = useRef<HTMLFormElement>(null);

  const { user, setUser } = useAuthStore();
  const profile = readStoredUser(user);
  const { trigger: triggerPostProfileUpdate } = usePostProfileUpdate();

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<Partial<FormData>>({});
  const [isSaving, setIsSaving] = useState(false);
  // Holds off a second save before the disabled buttons render. Released only
  // when a save fails, so the form can be sent again.
  const isSavingRef = useRef(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      height: profile.height,
      alcohol: profile.views_on_alcohol,
      smoke: profile.smoking_status,
      sport: profile.sports,
      children: profile.children,
    },
    resolver: async (data: any, context, options: any) => {
      const schemas: Record<number, any> = {
        0: heightSchema,
        1: alcoholSchema,
        2: smokeSchema,
        3: sportSchema,
        4: childrenSchema,
      };

      const schema = schemas[context.currentStep as number];
      return zodResolver(schema)(data, context, options);
    },
    context: { currentStep },
  });

  const saveAnswers = async (answers: Partial<FormData>) => {
    if (isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;

    if (Object.keys(answers).length === 0) {
      navigate({ to: '/profile/edit' });
      return;
    }

    setIsSaving(true);

    try {
      const updatedUser = await triggerPostProfileUpdate({
        height: answers.height,
        views_on_alcohol: answers.alcohol,
        smoking_status: answers.smoke,
        sports: answers.sport,
        children: answers.children,
      });

      if (isStorableUser(updatedUser)) {
        setUser(updatedUser);
        navigate({ to: '/profile/edit' });
        return;
      }

      // An answer without a user is a failed save: the stored user stays.
      reportRefusedUser('Short', updatedUser);
    } catch {
      // A failed request: handled below, as a save without a user is.
    }

    emit('error', {
      title: 'Данные не загрузились',
      description: 'Попробуйте ещё раз',
    });
    setCurrentStep(0);
    isSavingRef.current = false;
    setIsSaving(false);
  };

  const onSubmit = (data: FormData) => {
    const answers = { ...formData, ...data };

    setFormData(answers);

    if (currentStep < LAST_STEP) {
      setCurrentStep(currentStep + 1);
      return;
    }

    void saveAnswers(answers);
  };

  const skipStep = () => {
    if (currentStep < LAST_STEP) {
      setCurrentStep(currentStep + 1);
      return;
    }

    void saveAnswers(formData);
  };

  const requestSubmit = useRequestSubmit(formRef);

  useEffect(() => {
    tabbar.setBottomSheet(
      <BottomSheet
        buttons={[
          <Button
            testId="short-next-button"
            disabled={isSaving}
            loading={isSaving}
            onClick={() => requestSubmit()}
          >
            Далее
          </Button>,
          <Button
            testId="short-skip-button"
            variant="actionSecondary"
            disabled={isSaving}
            onClick={skipStep}
          >
            Пропустить
          </Button>,
        ]}
      />,
    );
  }, [currentStep, formData, isSaving, requestSubmit]);

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit(onSubmit)}
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {currentStep === 0 && (
        <>
          <Heading level={2} align="center">
            Ваш рост
          </Heading>
          {!errors.height && (
            <Paragraph variant="secondary" align="center" compact>
              От 140 до 210 см
            </Paragraph>
          )}
          {errors.height && (
            <Paragraph variant="negative" align="center" compact>
              {errors.height.message}
            </Paragraph>
          )}

          <Card
            testId="profile-short-height"
            transparent
            style={{ padding: '0 16px' }}
          >
            <Controller
              name="height"
              control={control}
              render={({ field }) => (
                <Input
                  placeholder="Введите ваш рост"
                  value={field.value}
                  onChange={(e) =>
                    field.onChange(Number(e.target.value) || undefined)
                  }
                  regexp={/^[0-9]*$/}
                />
              )}
            />
          </Card>
        </>
      )}

      {currentStep === 1 && (
        <>
          <Heading level={2} align="center">
            Взгляд на алкоголь
          </Heading>
          {!errors.alcohol && (
            <Paragraph variant="secondary" align="center" compact>
              Как часто пьете или вовсе нет?
            </Paragraph>
          )}
          {errors.alcohol && (
            <Paragraph variant="negative" align="center" compact>
              {errors.alcohol.message}
            </Paragraph>
          )}

          <Card
            testId="profile-short-alcohol"
            transparent
            style={{ padding: '0 16px' }}
          >
            <Controller
              name="alcohol"
              control={control}
              render={({ field }) => (
                <CellSelect
                  value={field.value}
                  items={Object.entries(dicts.alcoholViewDict).map(
                    ([value, label]) => ({
                      value,
                      label,
                    }),
                  )}
                  onChange={(value) => field.onChange(value)}
                />
              )}
            />
          </Card>
        </>
      )}

      {currentStep === 2 && (
        <>
          <Heading level={2} align="center">
            Курите или не курите
          </Heading>
          {!errors.smoke && (
            <Paragraph variant="secondary" align="center" compact>
              Пора решать
            </Paragraph>
          )}
          {errors.smoke && (
            <Paragraph variant="negative" align="center" compact>
              {errors.smoke.message}
            </Paragraph>
          )}

          <Card
            testId="profile-short-smoke"
            transparent
            style={{ padding: '0 16px' }}
          >
            <Controller
              name="smoke"
              control={control}
              render={({ field }) => (
                <CellSelect
                  value={field.value}
                  items={Object.entries(dicts.smokingStatusDict).map(
                    ([value, label]) => ({
                      value,
                      label,
                    }),
                  )}
                  onChange={(value) => field.onChange(value)}
                />
              )}
            />
          </Card>
        </>
      )}

      {currentStep === 3 && (
        <>
          <Heading level={2} align="center">
            Отношение к спорту
          </Heading>
          {!errors.sport && (
            <Paragraph variant="secondary" align="center" compact>
              Занимаетесь или отдыхаете?
            </Paragraph>
          )}
          {errors.sport && (
            <Paragraph variant="negative" align="center" compact>
              {errors.sport.message}
            </Paragraph>
          )}

          <Card
            testId="profile-short-sport"
            transparent
            style={{ padding: '0 16px' }}
          >
            <Controller
              name="sport"
              control={control}
              render={({ field }) => (
                <CellSelect
                  value={field.value}
                  items={Object.entries(dicts.sportsDict).map(
                    ([value, label]) => ({
                      value,
                      label,
                    }),
                  )}
                  onChange={(value) => field.onChange(value)}
                />
              )}
            />
          </Card>
        </>
      )}

      {currentStep === 4 && (
        <>
          <Heading level={2} align="center">
            Наличие детей
          </Heading>

          {!errors.children && (
            <Paragraph variant="secondary" align="center" compact>
              Родитель или чайлдфри?
            </Paragraph>
          )}
          {errors.children && (
            <Paragraph variant="negative" align="center" compact>
              {errors.children.message}
            </Paragraph>
          )}

          <Card
            testId="profile-short-children"
            transparent
            style={{ padding: '0 16px' }}
          >
            <Controller
              name="children"
              control={control}
              render={({ field }) => (
                <CellSelect
                  value={field.value}
                  items={Object.entries(dicts.childrenStatusDict).map(
                    ([value, label]) => ({
                      value,
                      label,
                    }),
                  )}
                  onChange={(value) => field.onChange(value)}
                />
              )}
            />
          </Card>
        </>
      )}
    </form>
  );
};
